import { useEffect, useRef, useState } from 'react'
import { useRuntimeConfig, isGptUrl } from '../../../lib/runtimeConfig.js'
import {
  GPT_BRIDGE, STEP_LABEL, failureText, provenanceLine, bridgeIsStale, staleBridgeText, BRIDGE_MIN_VERSION, BRIDGE_DOWNLOAD_URL,
} from './bridgeModel.js'
import { unownedReferences } from '../../../lib/ownedHosts.js'
import MarkRung from '../MarkRung.jsx'
import {
  detectBridge, runInGpt, jobReferences, downloadReferenceZip, attachGptImage, dataUrlToBlob,
} from './bridgeClient.js'
import styles from './GptBridgePanel.module.css'

/**
 * SEND THIS BUILD TO THE GPT, AND BRING THE RING BACK.
 *
 * One press when the SR GPT Bridge extension is running in this Chrome. The
 * same job by hand, in four numbered steps, ALWAYS visible underneath: the
 * browser path fails in ways nobody can fix mid-demo (a signed-out tab, a
 * ChatGPT page change, a window that collapses), and the person at the front
 * of the room should be one click from finishing rather than one error from
 * stuck. Both paths end at the same upload and the same provenance note.
 *
 * Props: buildId, view (engine view id), viewLabel, prompt, crestUrl,
 * onAttached(result) — result is attachGptImage()'s value.
 *
 * ⚠️ `target` NAMES WHICH GPT, and it is a prop rather than a constant because
 * there are now two jobs and they are not the same GPT. SR Artwork Engine
 * Director answers the assembled RING prompt; SR Bezel Drop Director draws the
 * SHEET. Sending one job to the other's GPT is a measured failure, not a
 * theory: artworkGpt answered the ring prompt with a written build spec on
 * 2026-09-18 and drew nothing. `requireBuild` is the second half of the same
 * change — a Bezel Drop is not a build, so there is no buildId to demand, and
 * demanding one would grey out the whole panel for a job that works.
 */
export default function GptBridgePanel({
  buildId, view, viewLabel, prompt, crestUrl, onAttached,
  target = 'artworkGpt', requireBuild = true, eyebrow = 'Render in your GPT · no API spend',
  referenceSet, processImage, preservePixels = false,
}) {
  const { config, loaded, setDeviceGpt } = useRuntimeConfig()
  const gpt = config[target] || config.artworkGpt
  const refs = referenceSet || jobReferences(config, crestUrl)
  // What actually goes to the GPT: the assembled prompt, with the configured
  // instruction line above it when one is set. Copy and one-press send the same text.
  const outbound = gpt.promptPrefix && prompt ? `${gpt.promptPrefix}\n\n${prompt}` : prompt

  const [bridge, setBridge] = useState({ checked: false, present: false, version: '' })
  const [run, setRun] = useState({ state: 'idle', step: '', detail: '', startedAt: 0 })
  const [msg, setMsg] = useState({ tone: '', text: '' })
  const [copied, setCopied] = useState(false)
  const [zipping, setZipping] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [editGpt, setEditGpt] = useState(false)
  const [gptDraft, setGptDraft] = useState('')
  const [last, setLast] = useState(null)
  const [imported, setImported] = useState(null)
  const fileRef = useRef(null)
  const alive = useRef(true)

  useEffect(() => {
    alive.current = true
    detectBridge().then((b) => { if (alive.current) setBridge({ checked: true, ...b }) })
    return () => { alive.current = false }
  }, [])

  const say = (tone, text) => alive.current && setMsg({ tone, text })

  async function finish(blob, route, chatUrl = '') {
    setRun((r) => ({ ...r, step: 'uploading' }))
    const finalBlob = processImage ? await processImage(blob) : blob
    const out = await attachGptImage({ buildId, view, blob: finalBlob, route, gptUrl: gpt.url, chatUrl, preservePixels })
    if (!alive.current) return
    setLast(out)
    onAttached?.(out)
    say(out.recordError ? 'warn' : 'ok',
      out.recordError
        ? `Attached and owned in HubSpot File Manager. The provenance note could not be written to the build record (${out.recordError}); the file name carries the same provenance.`
        : 'Attached, owned in HubSpot File Manager, and recorded on the build.')
    return out
  }

  /* ⚠️ THE PRE-FLIGHT, and the reason it is on THIS side of the wall.
   *
   * The extension refuses an unowned reference, correctly, but it does so after
   * it has already started the job — so the operator watches it spin and then
   * reads a failure. The app knows the same rule, knows it instantly, and can
   * name the file AND the fix before anything opens. Same rule, earlier, in
   * words. It does NOT replace the extension's check: that one is the wall, and
   * a page cannot be trusted to guard a wall on the extension's behalf. */
  const unowned = unownedReferences(refs)

  async function oneStep() {
    if (!gpt.url) { say('warn', failureText('no_gpt')); return }
    if (!bridge.present) { say('warn', failureText('no_extension')); return }
    if (bridgeIsStale(bridge.version)) { say('warn', staleBridgeText(bridge.version)); return }
    if (unowned.length) {
      say('warn', `${unowned[0].name} cannot be sent: ${unowned[0].reason} Nothing was sent to the GPT.`)
      return
    }
    setMsg({ tone: '', text: '' })
    setRun({ state: 'running', step: 'starting', detail: '', startedAt: Date.now() })
    const res = await runInGpt({ gptUrl: gpt.url, prompt: outbound, references: refs },
      (step, detail) => alive.current && setRun((r) => ({ ...r, step, detail })))
    if (!alive.current) return
    if (!res.ok) {
      setRun({ state: 'failed', step: res.step || '', detail: '', startedAt: 0 })
      say('warn', failureText(res.code, res.detail))
      return
    }
    try {
      await finish(await dataUrlToBlob(res.dataUrl), GPT_BRIDGE.ROUTE_AUTO, res.chatUrl)
      /* ⚠️ STATE THE SIZE THAT ACTUALLY ARRIVED. An import that silently lost
       * half its pixels looks exactly like one that did not, so the panel
       * prints the verified dimensions and byte count rather than letting the
       * operator assume the full file came back. */
      setImported(res.width && res.height
        ? { w: res.width, h: res.height, bytes: res.byteLength || 0, route: res.importRoute || '' }
        : null)
      setRun((r) => ({ state: 'done', step: 'done', detail: `${Math.round((Date.now() - r.startedAt) / 1000)}s`, startedAt: 0 }))
    } catch (e) {
      setRun({ state: 'failed', step: 'uploading', detail: '', startedAt: 0 })
      say('warn', failureText('upload_failed', e?.message))
    }
  }

  async function copyPrompt() {
    try {
      await navigator.clipboard.writeText(outbound || '')
      setCopied(true)
      setTimeout(() => alive.current && setCopied(false), 2500)
    } catch {
      say('warn', 'The browser refused the clipboard. Select the prompt text below and copy it by hand.')
    }
  }

  async function zipRefs() {
    setZipping(true)
    try {
      const n = await downloadReferenceZip(refs, `${buildId || 'build'}-${view}-references.zip`)
      say('ok', `Saved ${n} reference${n === 1 ? '' : 's'} as one .zip. Unzip it and attach the images in the GPT.`)
    } catch (e) {
      say('warn', `The references could not be bundled (${e?.message}). Open each one below and save it.`)
    } finally {
      setZipping(false)
    }
  }

  async function manualUpload(file) {
    if (!file) return
    setUploading(true)
    setMsg({ tone: '', text: '' })
    try {
      await finish(file, GPT_BRIDGE.ROUTE_MANUAL)
    } catch (e) {
      say('warn', `${e?.message || 'The upload failed.'} Nothing was attached.`)
    } finally {
      setUploading(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  function saveGpt(clear) {
    const ok = setDeviceGpt(clear ? '' : gptDraft, target)
    if (!ok) { say('warn', 'That is not a ChatGPT GPT link. It should look like https://chatgpt.com/g/g-…'); return }
    setEditGpt(false)
    say('ok', clear ? 'This device now uses the site GPT again.' : 'This device now sends to that GPT. Other devices are unchanged.')
  }

  const running = run.state === 'running'
  const stale = bridge.present && bridgeIsStale(bridge.version)
  const canAuto = bridge.present && !stale && !!gpt.url && !!prompt && !unowned.length && (!requireBuild || !!buildId)

  return (
    <section className={styles.panel} aria-label="Render in your GPT">
      <header className={styles.head}>
        <div>
          <div className={styles.eyebrow}>{eyebrow}</div>
          <div className={styles.target}>
            {gpt.url ? gpt.label : 'No GPT configured'}
            <span className={styles.source}> · {gpt.source}</span>
          </div>
        </div>
        <span className={styles.chip} data-on={bridge.present && !stale ? 'yes' : 'no'}>
          {!bridge.checked ? 'Checking for the bridge…'
            : !bridge.present ? 'Bridge not installed'
              : stale ? `Bridge v${bridge.version}, update available`
                : `Bridge ready${bridge.version ? ` v${bridge.version}` : ''}`}
        </span>
      </header>

      {/* ⚠️ A STALE EXTENSION IS NOT A MYSTERY ANY MORE. Publishing this site
          cannot update an unpacked extension, so the browser can be running a
          copy that predates the fix. The panel says which version is loaded,
          which one it needs, and where the Reload button is. */}
      {stale && <p className={styles.warn} role="status">{staleBridgeText(bridge.version)}</p>}
      {bridge.checked && (!bridge.present || stale) && <p className={styles.hint}><a href={BRIDGE_DOWNLOAD_URL} download>Download SR GPT Bridge v{BRIDGE_MIN_VERSION}</a>. Extract the ZIP, then load its folder in Chrome’s extension page or replace the files in your existing bridge folder and press Reload. Refresh this Studio tab afterwards.</p>}

      {/* THE SAME OWNERSHIP RULE THE EXTENSION ENFORCES, said here BEFORE the
          run rather than after it — and it names the file and the fix. */}
      {unowned.map((u) => (
        <p key={u.name + u.url} className={styles.warn} role="status">
          <strong>{u.name} is not an owned file yet.</strong> {u.reason}
        </p>
      ))}

      <button type="button" className={`btn btn-primary ${styles.primary}`} disabled={!canAuto || running} onClick={oneStep}>
        {running ? (STEP_LABEL[run.step] || 'Working') + '…' : `Render ${viewLabel || 'this view'} in my GPT`}
      </button>
      {running && (
        <p className={styles.progress} aria-live="polite">
          {STEP_LABEL[run.step] || run.step}{run.detail ? ` · ${run.detail}` : ''}. The ChatGPT tab shows it happening; this page
          fills in when the image arrives. The manual steps below still work.
        </p>
      )}
      {run.state === 'done' && (
        <p className={styles.ok}>
          Round trip {run.detail}.
          {imported
            ? ` Imported at ${imported.w} × ${imported.h}, ${(imported.bytes / 1048576).toFixed(2)} MB, `
              + `${imported.route === 'files-api'
                ? 'taken from the conversation record and checked against its declared size and dimensions'
                : 'read from the rendered image; the record route was unavailable, so the original size is not verified'}.`
            : ' The import reported no dimensions, so its resolution is unverified.'}
        </p>
      )}
      {!canAuto && bridge.checked && (
        <p className={styles.hint}>
          {!bridge.present
            ? 'One-press needs the SR GPT Bridge extension in this Chrome (it cannot run on a phone). The four steps below do the same job by hand.'
            : stale ? 'Install the bridge update above to use the current ChatGPT handoff.'
              : !gpt.url ? failureText('no_gpt')
              : unowned.length ? 'The mark above has to be an owned file before it can be sent. The reason is stated above the button.'
                : !prompt ? 'There is no prompt to send yet — supply the mark first.'
                  : 'Render the build first; the GPT needs its assembled prompt.'}
        </p>
      )}

      {msg.text && <p className={msg.tone === 'ok' ? styles.ok : styles.warn} role="status">{msg.text}</p>}

      {/* THE MANUAL ROUND TRIP. Permanently visible, not an error screen. */}
      <ol className={styles.steps} aria-label="Do it by hand">
        <li className={styles.step}>
          <span className={styles.num}>1</span>
          <div className={styles.body}>
            <button type="button" className={styles.action} onClick={copyPrompt} disabled={!prompt}>
              {copied ? 'Copied' : 'Copy the prompt'}
            </button>
            <details className={styles.more}>
              <summary>See the prompt ({(outbound || '').length.toLocaleString()} characters)</summary>
              <pre className={styles.pre}>{outbound}</pre>
            </details>
          </div>
        </li>

        <li className={styles.step}>
          <span className={styles.num}>2</span>
          <div className={styles.body}>
            {refs.length ? (
              <>
                <button type="button" className={styles.action} onClick={zipRefs} disabled={zipping}>
                  {zipping ? 'Bundling…' : `Save the ${refs.length} reference${refs.length === 1 ? '' : 's'} (.zip)`}
                </button>
                <div className={styles.refs}>
                  {refs.map((r) => (
                    <a key={r.url} href={r.url} target="_blank" rel="noopener noreferrer" className={styles.ref}>{r.name}</a>
                  ))}
                </div>
              </>
            ) : (
              <p className={styles.hint}>No references to attach for this job.</p>
            )}
            {loaded && !config.referenceSet.length && config.referenceSetNote && (
              <p className={styles.hint}>SR reference set: not configured yet. {config.referenceSetNote}</p>
            )}
          </div>
        </li>

        <li className={styles.step}>
          <span className={styles.num}>3</span>
          <div className={styles.body}>
            {gpt.url
              ? <a className={styles.action} href={gpt.url} target="_blank" rel="noopener noreferrer">Open {gpt.label}</a>
              : <span className={styles.hint}>No GPT configured.</span>}
            <span className={styles.hint}>Paste the prompt, attach the references, send, then save the image it draws.</span>
          </div>
        </li>

        <li className={styles.step}>
          <span className={styles.num}>4</span>
          <div className={styles.body}>
            <button type="button" className={styles.action} onClick={() => fileRef.current?.click()} disabled={uploading || (requireBuild && !buildId)}>
              {uploading ? 'Uploading to File Manager…' : 'Upload the image from the GPT'}
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className={styles.file}
              onChange={(e) => manualUpload(e.target.files?.[0])}
            />
          </div>
        </li>
      </ol>

      {/* THE RUNG TRAVELS WITH THE ASSET. Whatever view or sheet this panel
          just produced, it came out of a model, so it is rung 1 and the badge
          says so at the point the operator is most likely to save it. */}
      {last && <MarkRung markSource={last.mark} detail={provenanceLine(last)} />}

      <details className={styles.more} open={editGpt} onToggle={(e) => setEditGpt(e.currentTarget.open)}>
        <summary>Change the GPT for this device</summary>
        <div className={styles.gptRow}>
          <input
            className={`input ${styles.gptInput}`}
            placeholder="https://chatgpt.com/g/g-…"
            value={gptDraft}
            onChange={(e) => setGptDraft(e.target.value)}
            inputMode="url"
          />
          <button type="button" className={styles.action} disabled={!isGptUrl(gptDraft)} onClick={() => saveGpt(false)}>Use this GPT here</button>
          {gpt.source === 'this device' && (
            <button type="button" className={styles.action} onClick={() => saveGpt(true)}>Back to the site GPT</button>
          )}
        </div>
        <p className={styles.hint}>
          This browser only. Everyone else follows sr-runtime-config.json{gpt.siteUrl ? `, currently ${gpt.siteUrl}` : ''}.
        </p>
      </details>
    </section>
  )
}
